import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  CheckCircle, FileText, Calculator, AlertTriangle, MessageCircle, Shield,
  IndianRupee, RefreshCw, ArrowRight, Calendar,
} from "lucide-react";
import ContactForm from "../components/ContactForm";
import useJsonLd from "../hooks/useJsonLd";
import useCanonical from "../hooks/useCanonical";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";

const WHATSAPP_URL = "https://wa.me/918940988776?text=Hi%20Wintrust%2C%20I%20need%20E-Commerce%20GST%20Filing%20support.%20Please%20guide%20me.";
const WHATSAPP_BOTTOM_URL = "https://wa.me/918940988776?text=Hi%20Wintrust%2C%20I%20need%20E-Commerce%20GST%20Filing%20support.";

const RETURN_TYPES = [
  { title: "GSTR-1", desc: "Invoice-wise outward supply details — every B2B invoice, B2C sale, export, and credit/debit note. Filed monthly by the 11th. Must match your marketplace sales reports exactly." },
  { title: "GSTR-3B", desc: "Monthly summary return with tax payment. Includes taxable sales, input tax credit (ITC) utilization, and TCS credit claims. Due by the 20th. Since January 2026, the portal blocks filing if ITC doesn't match GSTR-2B." },
  { title: "GSTR-9", desc: "Annual return — comprehensive yearly filing summarizing all monthly returns, ITC reconciliation, and tax adjustments for the entire financial year." },
  { title: "GSTR-2B Reconciliation", desc: "Auto-generated statement showing your eligible ITC and TCS credits from marketplace GSTR-8 filings. We verify every entry to ensure you claim all credits and catch mismatches before they trigger notices." },
];

const SERVICES = [
  { icon: <FileText size={24} />, title: "Monthly GSTR-1 Filing", desc: "We prepare and file your GSTR-1 with invoice-wise outward supply details, ensuring every B2B invoice, B2C sale, and credit/debit note is accurately reported and matches marketplace data." },
  { icon: <Calculator size={24} />, title: "Monthly GSTR-3B Filing", desc: "Summary return preparation including taxable sales, ITC utilization, TCS credit claims, and tax payment calculation. We follow the correct sequence: GSTR-1 → IMS review → GSTR-2B check → GSTR-3B filing." },
  { icon: <RefreshCw size={24} />, title: "TCS Reconciliation (Amazon, Flipkart, Meesho)", desc: "We download and match your marketplace settlement reports with GSTR-8 data to ensure every rupee of TCS deducted is correctly claimed in your electronic cash ledger. No credit left unclaimed." },
  { icon: <Shield size={24} />, title: "Marketplace Data Reconciliation", desc: "We match sales, returns, commissions, shipping fees, and tax components from each platform's reports with your GST data. This prevents the GSTR-1 vs. GSTR-3B mismatches that trigger Rule 88C notices." },
  { icon: <IndianRupee size={24} />, title: "ITC (Input Tax Credit) Optimization", desc: "We review GSTR-2B to ensure you claim ITC on all eligible business expenses — packaging, logistics, advertising, platform commissions, and more. Smart ITC planning significantly reduces your net GST burden." },
  { icon: <CheckCircle size={24} />, title: "Annual Return (GSTR-9) Filing", desc: "Comprehensive yearly filing that summarizes all monthly returns, reconciles ITC, and presents accurate financial-year-level data. Filed with meticulous cross-verification against your books." },
];

const CALENDAR = [
  { date: "1st – 5th", task: "Download marketplace reports & settlement statements" },
  { date: "6th – 9th", task: "Reconcile sales, returns, commissions, TCS with accounting records" },
  { date: "10th", task: "Marketplace GSTR-8 (TCS return) deadline" },
  { date: "11th", task: "GSTR-1 filing deadline", highlight: true },
  { date: "13th", task: "IMS (Invoice Management System) review deadline" },
  { date: "14th", task: "GSTR-2B auto-generated by portal" },
  { date: "17th", task: "Final GSTR-2B reconciliation with purchase register" },
  { date: "20th", task: "GSTR-3B filing deadline", highlight: true },
];

const MISTAKES = [
  { title: "Not Reconciling TCS Credits", body: "Many sellers never check Table 9 of GSTR-3B for TCS. At 1% of total marketplace sales, this means thousands of rupees sitting unclaimed every year." },
  { title: "GSTR-1 and GSTR-3B Mismatch", body: "If sales reported in GSTR-1 don't match GSTR-3B, the GST system flags it via Rule 88C and issues an automated notice. This is the #1 reason for scrutiny among e-commerce sellers." },
  { title: "Ignoring Returns and Credit Notes", body: "Customer returns must be adjusted through credit notes within the same financial year. If not, you end up paying GST on sales that were reversed." },
  { title: "Not Claiming ITC on Marketplace Commissions", body: "Amazon, Flipkart, and Meesho charge 18% GST on their commissions. This is eligible for ITC. Many sellers miss this and overpay their GST." },
  { title: "Filing GSTR-3B Before GSTR-2B Review", body: "Since January 2026, the portal blocks GSTR-3B filing if ITC claims don't match GSTR-2B. Filing without reviewing GSTR-2B first results in a blocked return and missed deadline." },
];

const FAQS = [
  { q: "What GST returns must e-commerce sellers file?", a: "E-commerce sellers must file GSTR-1 (outward supplies) and GSTR-3B (summary return + tax payment) every month. GSTR-9 (annual return) is filed once per year. These are mandatory even for months with zero sales." },
  { q: "What is TCS and how do I claim it?", a: "TCS (Tax Collected at Source) is 1% deducted by marketplaces on your net taxable sales. It appears in your electronic cash ledger after the marketplace files their GSTR-8. You claim it as credit in GSTR-3B to reduce your tax liability." },
  { q: "What happens if I miss the filing deadline?", a: "Late filing of GSTR-3B attracts ₹50/day (₹20 for nil returns) as late fee, plus 18% annual interest on any outstanding tax. Continuous non-filing can block e-way bill generation." },
  { q: "Do I need to file even if I had no sales in a month?", a: "Yes. Nil returns (GSTR-1 and GSTR-3B) are mandatory every month even with zero sales. Non-filing blocks subsequent month's returns." },
  { q: "How do you handle multi-platform reconciliation?", a: "We download settlement reports from each marketplace (Amazon, Flipkart, Meesho, etc.), reconcile sales, returns, commissions, shipping fees, and TCS deductions, then match everything with your GST data before filing." },
  { q: "Can e-commerce sellers use the QRMP scheme for quarterly filing?", a: "Sellers with annual turnover up to ₹5 crore may qualify for quarterly filing under the QRMP scheme. However, IFF (Invoice Furnishing Facility) must still be used to upload B2B invoices in months 1 and 2 of each quarter." },
  { q: "What is the penalty for GSTR-1 and GSTR-3B mismatch?", a: "The GST system flags mismatches via Rule 88C and issues a notice asking for clarification. If unresolved, the tax difference plus 18% interest is demanded. Repeated mismatches invite detailed scrutiny." },
];

const EcomGSTFilingPage = () => {
  useCanonical("/ecom-gst-filing");
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "E-Commerce GST Filing in Coimbatore",
    "provider": { "@type": "LocalBusiness", "name": "WINTRUST SOLUTIONS" },
    "areaServed": { "@type": "City", "name": "Coimbatore" },
    "description": "Monthly GSTR-1, GSTR-3B filing and TCS reconciliation for e-commerce sellers on Amazon, Flipkart, Meesho. Avoid penalties, claim TCS credits.",
  });
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQS.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  });

  return (
    <>
      <Helmet>
        <title>E-Commerce GST Filing & Return Services in Coimbatore | TCS Reconciliation | WINTRUST SOLUTIONS</title>
        <meta name="description" content="Monthly GSTR-1, GSTR-3B filing & TCS reconciliation for e-commerce sellers on Amazon, Flipkart, Meesho. Avoid penalties, claim TCS credits. Expert GST compliance — WINTRUST SOLUTIONS." />
        <meta property="og:title" content="E-Commerce GST Filing & TCS Reconciliation | Wintrust Solutions Coimbatore" />
        <meta property="og:description" content="Monthly GST return filing, TCS credit tracking, and marketplace reconciliation for online sellers. Never miss a deadline." />
      </Helmet>

      {/* Hero */}
      <section className="bg-navy py-16">
        <div className="container-custom">
          <div className="max-w-3xl">
            <nav className="text-slate-400 text-sm mb-4" data-testid="ecom-fil-breadcrumb">
              <Link to="/" className="hover:text-gold">Home</Link> /{" "}
              <span className="text-slate-300">Services</span> /{" "}
              <span className="text-white">E-Commerce GST Filing</span>
            </nav>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-heading" data-testid="ecom-fil-h1">
              E-Commerce GST Filing Services in Coimbatore | Accurate & Timely
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Selling on Amazon, Flipkart, or Meesho? E-commerce GST compliance involves monthly return filing, TCS reconciliation, and marketplace data matching — one mismatch triggers notices. At WINTRUST SOLUTIONS, we handle your complete e-commerce GST filing so you stay compliant and never lose TCS credits.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#contact" className="btn-primary" data-testid="ecom-fil-cta-primary">Get a Quote</a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn-whatsapp" data-testid="ecom-fil-cta-whatsapp">
                <MessageCircle size={18} /> WhatsApp Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Return Types */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading">
            Monthly, Quarterly, and Annual GST Return Filing
          </h2>
          <p className="text-slate-600 mb-8">We handle all types of GST returns for e-commerce sellers, including:</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {RETURN_TYPES.map((item, i) => (
              <div key={i} className="p-6 bg-slate-50 rounded-lg border-l-4 border-gold">
                <h3 className="font-semibold text-navy mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Timely Filing */}
      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading">
            Why Timely E-Commerce GST Filing Matters
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: <AlertTriangle size={32} />, title: "Avoid Penalties & Interest", desc: "Late filing attracts ₹50 per day (₹20 for nil returns) plus 18% annual interest on outstanding tax. Continuous non-filing blocks e-way bill generation and can freeze your marketplace payouts." },
              { icon: <IndianRupee size={32} />, title: "Claim Your TCS Credits", desc: "Marketplaces deduct 1% TCS on every sale. At ₹15 lakh monthly sales, that's ₹1,800/month sitting unclaimed if you don't reconcile and file on time. Over a year, that's ₹21,600+ in working capital you're leaving on the table." },
              { icon: <Shield size={32} />, title: "Prevent Mismatch Notices", desc: "The GST system auto-flags differences between GSTR-1 and GSTR-3B via Rule 88C. Marketplace data must match your filed returns. One mismatch = scrutiny notice + interest liability. Professional reconciliation prevents this." },
            ].map((item, i) => (
              <div key={i} className="text-center p-6 bg-white rounded-lg shadow-sm">
                <div className="text-gold mb-4 flex justify-center">{item.icon}</div>
                <h3 className="font-semibold text-navy mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Include */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading">
            Our E-Commerce GST Filing Services Include
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="ecom-fil-services">
            {SERVICES.map((s, i) => (
              <div key={i} className="bg-slate-50 rounded-lg p-6 border border-slate-100 hover:border-gold/40 transition-colors">
                <div className="text-gold mb-3">{s.icon}</div>
                <h3 className="font-semibold text-navy mb-2">{s.title}</h3>
                <p className="text-slate-600 text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading">
            Our E-Commerce GST Filing Process
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Data Collection (Days 1–5)", desc: "We collect monthly sales reports, settlement statements, and TCS data from all your marketplace seller dashboards (Amazon, Flipkart, Meesho, etc.) and reconcile them with your accounting records." },
              { step: "02", title: "Reconciliation & Verification (Days 6–10)", desc: "We reconcile marketplace data with your sales register, verify TCS credits against GSTR-8 filings by operators, review GSTR-2B for ITC eligibility, and calculate your net taxable value after adjusting for returns and credit notes." },
              { step: "03", title: "Return Preparation & Filing (Days 11–20)", desc: "GSTR-1 is filed by the 11th, followed by IMS dashboard review and GSTR-2B reconciliation. GSTR-3B is then prepared and filed by the 20th with accurate tax liability, ITC utilization, and TCS credit claims." },
              { step: "04", title: "Confirmation & Reporting", desc: "You receive filing confirmation (ARN), a monthly compliance summary, and a TCS credit tracking report. We flag any pending credits or discrepancies for resolution in the next filing cycle." },
            ].map((item, i) => (
              <div key={i} className="text-center bg-white rounded-lg p-5 border border-slate-100">
                <div className="w-14 h-14 bg-navy rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-gold font-bold">{item.step}</span>
                </div>
                <h3 className="font-semibold text-navy mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Calendar */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <div className="flex items-center gap-3 mb-3">
            <Calendar className="text-gold" size={28} />
            <h2 className="text-3xl font-semibold text-navy font-heading">
              E-Commerce Seller GST Compliance Calendar
            </h2>
          </div>
          <p className="text-slate-600 mb-8">A typical month for an e-commerce seller looks like this:</p>
          <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden max-w-3xl" data-testid="ecom-fil-calendar">
            {CALENDAR.map((row, i) => (
              <div
                key={i}
                className={`flex items-center gap-4 px-5 py-4 ${
                  i < CALENDAR.length - 1 ? "border-b border-slate-200" : ""
                } ${row.highlight ? "bg-gold/10" : ""}`}
              >
                <span
                  className={`flex-shrink-0 w-24 md:w-28 text-sm font-bold ${
                    row.highlight ? "text-navy" : "text-gold"
                  }`}
                >
                  {row.date}
                </span>
                <span className={`text-sm md:text-base ${row.highlight ? "text-navy font-semibold" : "text-slate-700"}`}>
                  {row.task}
                </span>
              </div>
            ))}
          </div>
          <p className="text-slate-600 text-sm mt-5 max-w-3xl italic">
            With Wintrust Solutions managing your filings, this entire process takes less than 2 hours of your time per month.
          </p>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading">
            Common Mistakes E-Commerce Sellers Make in GST Filing
          </h2>
          <div className="grid md:grid-cols-2 gap-5">
            {MISTAKES.map((m, i) => (
              <div key={i} className="bg-white border-l-4 border-red-500 rounded-lg p-5">
                <div className="flex items-start gap-3">
                  <span className="bg-red-500/10 text-red-600 font-bold rounded-full w-7 h-7 flex items-center justify-center text-sm flex-shrink-0">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="font-semibold text-navy mb-1.5">{m.title}</h3>
                    <p className="text-slate-600 text-sm">{m.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white section-padding">
        <div className="container-custom max-w-4xl">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading text-center">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-3" data-testid="ecom-fil-faq">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`q-${i}`}
                className="bg-slate-50 rounded-lg border border-slate-200 px-6"
                data-testid={`ecom-fil-faq-${i}`}
              >
                <AccordionTrigger className="text-left text-navy font-semibold py-4 hover:no-underline hover:text-gold">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-4">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Internal Links */}
      <section className="bg-slate-50 py-10">
        <div className="container-custom">
          <h2 className="text-xl md:text-2xl font-semibold text-navy mb-5 font-heading">Related Services</h2>
          <div className="grid md:grid-cols-3 gap-3">
            <Link to="/ecom-gst-registration" className="bg-white border border-slate-200 hover:border-gold hover:text-gold rounded-xl px-5 py-4 text-navy text-sm font-medium inline-flex items-center justify-between gap-2 transition-colors">
              <span>E-Commerce GST Registration</span><ArrowRight size={16} />
            </Link>
            <Link to="/gst-return-filing" className="bg-white border border-slate-200 hover:border-gold hover:text-gold rounded-xl px-5 py-4 text-navy text-sm font-medium inline-flex items-center justify-between gap-2 transition-colors">
              <span>GST Return Filing (General)</span><ArrowRight size={16} />
            </Link>
            <Link to="/itr-filing" className="bg-white border border-slate-200 hover:border-gold hover:text-gold rounded-xl px-5 py-4 text-navy text-sm font-medium inline-flex items-center justify-between gap-2 transition-colors">
              <span>ITR Filing</span><ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-navy py-14">
        <div className="container-custom text-center">
          <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4 font-heading">
            Stop Losing TCS Credits. Start Filing Right.
          </h2>
          <p className="text-slate-300 mb-7 max-w-2xl mx-auto">
            E-commerce GST filing is complex — but it doesn't have to be your problem. Contact WINTRUST SOLUTIONS today and let our experts handle your monthly filing, TCS reconciliation, and marketplace compliance from end to end.
          </p>
          <a
            href={WHATSAPP_BOTTOM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-whatsapp inline-flex"
            data-testid="ecom-fil-bottom-cta"
          >
            <MessageCircle size={18} /> Get Expert GST Support
          </a>
        </div>
      </section>

      <ContactForm />
    </>
  );
};

export default EcomGSTFilingPage;
